/**
 * useEingangTasks - every task one Eingang produced (GET /tasks?comm_id=).
 *
 * The Maske works an Eingang, not a task: one mail can carry two policies and become
 * two tasks, and the broker approves them together. The existing comm_id filter answers
 * that - no new endpoint. Mail-team gated server-side.
 *
 * Mock mode used to return AUFGABEN_MOCK unfiltered (every row there has comm_id null),
 * so every Eingang showed all ten queue tasks. EINGANG_TASKS_MOCK makes the filter real.
 */

import { useQuery } from '@tanstack/react-query';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { fetchTaskRows } from '../../aufgaben/api/aufgaben-api';
import type { TaskRow } from '../../aufgaben/types';
import { eingangTasksMock } from '../mock';

export interface EingangTasksResult {
  rows: TaskRow[];
  /** Server total across all pages (include_total); null when the wire omits it. */
  total: number | null;
}

/** The mock leg: only the tasks THIS Eingang produced (empty for an unknown id). */
function mockResultFor(commId: string | null): EingangTasksResult {
  const rows = commId === null ? [] : eingangTasksMock(commId);
  return { rows, total: rows.length };
}

/**
 * The tasks of one Eingang. `commId` null fetches nothing - that is how the maske
 * holds off until a communication is selected.
 */
export function useEingangTasks(commId: string | null) {
  const live = isLiveData();
  return useQuery<EingangTasksResult>({
    // `eingang` scope segment keeps this key distinct from the Aufgaben queue and the
    // Eingang inbox, which hit the same GET /tasks with different params.
    queryKey: qk.inbox({ eingang: true, comm_id: commId }),
    queryFn: () =>
      live && commId !== null
        ? fetchTaskRows({ comm_id: commId })
        : Promise.resolve(mockResultFor(commId)),
    enabled: commId !== null,
    ...(live ? {} : { initialData: () => mockResultFor(commId), staleTime: Infinity }),
  });
}
